<script setup>
/**
 * This component handles parsing and sending WhatsApp message templates.
 * It works as follows:
 * 1. Displays the template text with variable placeholders.
 * 2. Generates input fields for each variable in the template.
 * 3. Validates that all variables are filled before sending.
 * 4. Replaces placeholders with user-provided values.
 * 5. Emits events to send the processed message or reset the template.
 */
import { ref, computed, onMounted, watch } from 'vue';
import { useVuelidate } from '@vuelidate/core';
import { requiredIf } from '@vuelidate/validators';
import { useI18n } from 'vue-i18n';

import Input from 'dashboard/components-next/input/Input.vue';
import {
  buildTemplateParameters,
  allKeysRequired,
  replaceTemplateVariables,
  DEFAULT_LANGUAGE,
  DEFAULT_CATEGORY,
  COMPONENT_TYPES,
  MEDIA_FORMATS,
  findComponentByType,
} from 'dashboard/helper/templateHelper';

const props = defineProps({
  template: {
    type: Object,
    default: () => ({}),
    validator: value => {
      if (!value || typeof value !== 'object') return false;
      if (!value.components || !Array.isArray(value.components)) return false;
      return true;
    },
  },
  contextVariables: {
    type: Object,
    default: () => ({}),
  },
});

const emit = defineEmits([
  'sendMessage',
  'replaceMessage',
  'validationChange',
  'resetTemplate',
  'back',
]);

const { t } = useI18n();

const processedParams = ref({});
const carouselScroller = ref(null);
const previewAction = ref('');

const languageLabel = computed(() => {
  return `${t('WHATSAPP_TEMPLATES.PARSER.LANGUAGE')}: ${props.template.language || DEFAULT_LANGUAGE}`;
});

const categoryLabel = computed(() => {
  return `${t('WHATSAPP_TEMPLATES.PARSER.CATEGORY')}: ${props.template.category || DEFAULT_CATEGORY}`;
});

const headerComponent = computed(() => {
  return findComponentByType(props.template, COMPONENT_TYPES.HEADER);
});

const bodyComponent = computed(() => {
  return findComponentByType(props.template, COMPONENT_TYPES.BODY);
});

const carouselComponent = computed(() => {
  return findComponentByType(props.template, COMPONENT_TYPES.CAROUSEL);
});

const carouselCards = computed(() => carouselComponent.value?.cards || []);

const hasCarousel = computed(() => carouselCards.value.length > 0);

const isOrderDetailsTemplate = computed(() => {
  if (props.template.display_format === 'ORDER_DETAILS') return true;

  return props.template.components?.some(component =>
    component.buttons?.some(button => button.type === 'ORDER_DETAILS')
  );
});

const bodyText = computed(() => {
  return bodyComponent.value?.text || '';
});

const hasMediaHeader = computed(() =>
  MEDIA_FORMATS.includes(headerComponent.value?.format)
);

const formatType = computed(() => {
  const format = headerComponent.value?.format;
  return format ? format.charAt(0) + format.slice(1).toLowerCase() : '';
});

const isDocumentTemplate = computed(() => {
  return headerComponent.value?.format?.toLowerCase() === 'document';
});

const hasVariables = computed(() => {
  return bodyText.value?.match(/{{([^}]+)}}/g) !== null;
});

const renderedTemplate = computed(() => {
  return replaceTemplateVariables(bodyText.value, processedParams.value);
});

const orderDetailsAmount = computed(() => {
  const amountVariable = bodyText.value.match(/R\$\s*{{([^}]+)}}/);
  if (!amountVariable) {
    return t('WHATSAPP_TEMPLATES.PARSER.ORDER_DETAILS.CURRENCY');
  }

  const key = amountVariable[1].trim();
  const value = processedParams.value.body?.[key] || `{{${key}}}`;
  return t('WHATSAPP_TEMPLATES.PARSER.ORDER_DETAILS.AMOUNT', { value });
});

const componentByType = (components, type) =>
  components?.find(component => component.type === type);

const cardBody = card => componentByType(card.components, COMPONENT_TYPES.BODY);

const cardButtons = card =>
  componentByType(card.components, COMPONENT_TYPES.BUTTONS)?.buttons || [];

const cardImageUrl = card => {
  const header = componentByType(card.components, COMPONENT_TYPES.HEADER);
  const example = header?.example || {};
  const source =
    example.header_url || example.header_handle || example.media_url;
  const value = Array.isArray(source) ? source[0] : source;

  return /^https?:\/\//i.test(value) ? value : null;
};

const renderCarouselText = (text, cardIndex) => {
  const values = processedParams.value.carousel?.[cardIndex]?.body || {};

  return (text || '').replace(/{{([^}]+)}}/g, (match, variable) => {
    const value = values[variable.trim()];
    return value || match;
  });
};

const hasCarouselVariables = computed(() =>
  carouselCards.value.some(card => {
    const hasBodyVariables = /{{([^}]+)}}/.test(cardBody(card)?.text || '');
    const hasUrlVariables = cardButtons(card).some(
      button => button.type === 'URL' && /{{([^}]+)}}/.test(button.url || '')
    );
    return hasBodyVariables || hasUrlVariables;
  })
);

const hasCarouselParameters = computed(() =>
  carouselCards.value.some((card, cardIndex) => {
    const bodyParameters = Object.keys(
      processedParams.value.carousel?.[cardIndex]?.body || {}
    );
    const buttonParameters = (
      processedParams.value.carousel?.[cardIndex]?.buttons || []
    ).some(Boolean);

    return bodyParameters.length > 0 || buttonParameters;
  })
);

const isBlank = value =>
  value === undefined || value === null || `${value}`.trim() === '';

const hasBlankParameters = parameters =>
  Object.values(parameters || {}).some(isBlank);

const hasBlankButtonParameters = buttons =>
  (buttons || []).filter(Boolean).some(button => isBlank(button.parameter));

const isFormInvalid = computed(() => {
  if (hasMediaHeader.value && !processedParams.value.header?.media_url) {
    return true;
  }

  if (hasBlankParameters(processedParams.value.body)) return true;
  if (hasBlankButtonParameters(processedParams.value.buttons)) return true;

  if (Array.isArray(processedParams.value.carousel)) {
    const hasEmptyCarouselVariable = processedParams.value.carousel.some(
      card =>
        hasBlankParameters(card?.body) ||
        hasBlankButtonParameters(card?.buttons)
    );
    if (hasEmptyCarouselVariable) return true;
  }

  return false;
});

watch(
  isFormInvalid,
  invalid => {
    emit('validationChange', invalid);
  },
  { immediate: true }
);

const availableContextVariables = computed(() =>
  Object.entries(props.contextVariables).filter(([, value]) => !isBlank(value))
);

const contextVariableLabel = key =>
  ({ nome: 'Nome do contato', telefone: 'Telefone', email: 'E-mail' })[key] ||
  key;

const contextVariablePrompt = key => `Usar ${contextVariableLabel(key)}`;

const carouselPreviousLabel = 'Mostrar cartões anteriores';
const carouselNextLabel = 'Mostrar próximos cartões';
const carouselPreviewLabel = 'Prévia dos cartões do carrossel';
const carouselTitle = 'Carrossel';
const carouselPreviousIcon = '‹';
const carouselNextIcon = '›';

const carouselImageAlt = cardIndex => `Imagem do cartão ${cardIndex + 1}`;
const carouselCardLabel = cardIndex => `Cartão ${cardIndex + 1}`;
const carouselVariablePlaceholder = key => `Variável ${key}`;
const carouselButtonPlaceholder = buttonIndex =>
  `Parâmetro do botão ${buttonIndex + 1}`;

const previewActionLabel = computed(() =>
  previewAction.value
    ? `Ação selecionada na prévia: ${previewAction.value}.`
    : ''
);

const applyContextValue = (target, key, value) => {
  target[key] = value;
};

const scrollCarousel = direction => {
  carouselScroller.value?.scrollBy({
    left: direction * 288,
    behavior: 'smooth',
  });
};

const carouselButtonUrl = (button, cardIndex, buttonIndex) => {
  if (button.type !== 'URL' || !button.url) return '';

  const parameter =
    processedParams.value.carousel?.[cardIndex]?.buttons?.[buttonIndex]
      ?.parameter;

  return button.url.replace(/{{[^}]+}}/g, parameter || '');
};

const useCarouselButton = (button, cardIndex, buttonIndex) => {
  const url = carouselButtonUrl(button, cardIndex, buttonIndex);
  if (url && /^https?:\/\//i.test(url)) {
    window.open(url, '_blank', 'noopener,noreferrer');
    return;
  }

  previewAction.value = button.text || button.type;
};

const v$ = useVuelidate(
  {
    processedParams: {
      requiredIfKeysPresent: requiredIf(hasVariables),
      allKeysRequired,
    },
  },
  { processedParams }
);

const initializeTemplateParameters = () => {
  processedParams.value = buildTemplateParameters(
    props.template,
    hasMediaHeader.value
  );
};

const updateMediaUrl = value => {
  processedParams.value.header ??= {};
  processedParams.value.header.media_url = value;
};

const updateMediaName = value => {
  processedParams.value.header ??= {};
  processedParams.value.header.media_name = value;
};

const buildMessagePayload = () => {
  const {
    name,
    category,
    language,
    namespace,
    is_custom: isCustom,
    custom_template_id: customTemplateId,
  } = props.template;

  return {
    message: renderedTemplate.value,
    templateParams: {
      name,
      category,
      language,
      namespace,
      processed_params: processedParams.value,
      ...(isCustom && {
        is_custom: true,
        custom_template_id: customTemplateId,
        // The server rebuilds custom templates from its approved source of
        // truth, including carousel cards, media and dynamic buttons.
        active_message_variables: processedParams.value.body || {},
        active_message_media_url: processedParams.value.header?.media_url,
      }),
    },
  };
};

const sendMessage = () => {
  v$.value.$touch();
  if (isFormInvalid.value) return;

  emit('sendMessage', buildMessagePayload());
};

const replaceMessage = () => {
  emit('replaceMessage', buildMessagePayload());
};

const resetTemplate = () => {
  emit('resetTemplate');
};

const goBack = () => {
  emit('back');
};

onMounted(initializeTemplateParameters);

watch(
  () => props.template,
  () => {
    initializeTemplateParameters();
    v$.value.$reset();
  },
  { deep: true }
);

defineExpose({
  processedParams,
  hasVariables,
  hasMediaHeader,
  isDocumentTemplate,
  headerComponent,
  renderedTemplate,
  v$,
  updateMediaUrl,
  updateMediaName,
  sendMessage,
  replaceMessage,
  resetTemplate,
  goBack,
});
</script>

<template>
  <div>
    <div class="flex flex-col gap-4 p-4 mb-4 rounded-lg bg-n-alpha-black2">
      <div class="flex justify-between items-center">
        <h3 class="text-sm font-medium text-n-slate-12">
          {{ template.name }}
        </h3>
        <span class="text-xs text-n-slate-11">
          {{ languageLabel }}
        </span>
      </div>

      <div v-if="isOrderDetailsTemplate" class="order-details-preview">
        <p class="order-details-eyebrow">
          {{ $t('WHATSAPP_TEMPLATES.PARSER.ORDER_DETAILS.HEADING') }}
        </p>
        <div class="order-details-main">
          <span class="order-details-icon" aria-hidden="true" />
          <div>
            <p class="order-details-title">
              {{ $t('WHATSAPP_TEMPLATES.PARSER.ORDER_DETAILS.INVOICE') }}
            </p>
            <p class="order-details-subtitle">
              {{ $t('WHATSAPP_TEMPLATES.PARSER.ORDER_DETAILS.QUANTITY') }}
            </p>
          </div>
        </div>
        <div class="order-details-row">
          <span>
            {{ $t('WHATSAPP_TEMPLATES.PARSER.ORDER_DETAILS.PAY_WITH') }}
          </span>
          <span class="order-details-qr" aria-hidden="true" />
        </div>
        <div class="order-details-row order-details-total">
          <span>{{ $t('WHATSAPP_TEMPLATES.PARSER.ORDER_DETAILS.TOTAL') }}</span>
          <strong>{{ orderDetailsAmount }}</strong>
        </div>
      </div>

      <div class="flex flex-col gap-2">
        <div class="rounded-md">
          <div class="text-sm whitespace-pre-wrap text-n-slate-12">
            {{ renderedTemplate }}
          </div>
        </div>
      </div>

      <div v-if="hasCarousel" class="carousel-preview">
        <button
          type="button"
          class="carousel-control carousel-control-left"
          :aria-label="carouselPreviousLabel"
          @click="scrollCarousel(-1)"
        >
          {{ carouselPreviousIcon }}
        </button>
        <div
          ref="carouselScroller"
          class="carousel-scroll flex gap-3 px-10 pb-1 overflow-x-auto"
          tabindex="0"
          :aria-label="carouselPreviewLabel"
        >
          <article
            v-for="(card, cardIndex) in carouselCards"
            :key="`carousel-card-${cardIndex}`"
            class="carousel-card w-64 overflow-hidden rounded-lg border shrink-0 border-n-weak bg-n-solid-2"
          >
            <img
              v-if="cardImageUrl(card)"
              :src="cardImageUrl(card)"
              :alt="carouselImageAlt(cardIndex)"
              class="object-cover w-full h-32 bg-n-alpha-black2"
            />
            <div
              v-else
              class="flex items-center justify-center h-24 text-xs text-n-slate-11 bg-n-alpha-black2"
            >
              {{ carouselCardLabel(cardIndex) }}
            </div>
            <div class="flex flex-col gap-2 p-3">
              <p class="text-sm whitespace-pre-wrap text-n-slate-12">
                {{ renderCarouselText(cardBody(card)?.text, cardIndex) }}
              </p>
              <div v-if="cardButtons(card).length" class="flex flex-wrap gap-2">
                <button
                  v-for="(button, buttonIndex) in cardButtons(card)"
                  :key="`carousel-card-${cardIndex}-button-${buttonIndex}`"
                  type="button"
                  class="px-2 py-1 text-xs rounded bg-n-alpha-black2 text-n-slate-11 hover:bg-n-alpha-3 focus:outline-none focus:ring-2 focus:ring-n-brand"
                  @click="useCarouselButton(button, cardIndex, buttonIndex)"
                >
                  {{ button.text || button.type }}
                </button>
              </div>
            </div>
          </article>
        </div>
        <button
          type="button"
          class="carousel-control carousel-control-right"
          :aria-label="carouselNextLabel"
          @click="scrollCarousel(1)"
        >
          {{ carouselNextIcon }}
        </button>
        <p v-if="previewAction" class="mt-2 text-xs text-n-slate-11">
          {{ previewActionLabel }}
        </p>
      </div>

      <div class="text-xs text-n-slate-11">
        {{ categoryLabel }}
      </div>
    </div>

    <div v-if="hasVariables || hasMediaHeader || hasCarouselVariables">
      <div v-if="hasMediaHeader" class="mb-4">
        <p class="mb-2.5 text-sm font-semibold">
          {{
            $t('WHATSAPP_TEMPLATES.PARSER.MEDIA_HEADER_LABEL', {
              type: formatType,
            }) || `${formatType} Header`
          }}
        </p>
        <div class="flex items-center mb-2.5">
          <Input
            :model-value="processedParams.header?.media_url || ''"
            type="url"
            class="flex-1"
            :placeholder="
              t('WHATSAPP_TEMPLATES.PARSER.MEDIA_URL_LABEL', {
                type: formatType,
              })
            "
            @update:model-value="updateMediaUrl"
          />
        </div>
        <div v-if="isDocumentTemplate" class="flex items-center mb-2.5">
          <Input
            :model-value="processedParams.header?.media_name || ''"
            type="text"
            class="flex-1"
            :placeholder="
              t('WHATSAPP_TEMPLATES.PARSER.DOCUMENT_NAME_PLACEHOLDER')
            "
            @update:model-value="updateMediaName"
          />
        </div>
      </div>

      <!-- Body Variables Section -->
      <div v-if="processedParams.body">
        <p class="mb-2.5 text-sm font-semibold">
          {{ $t('WHATSAPP_TEMPLATES.PARSER.VARIABLES_LABEL') }}
        </p>
        <div
          v-for="(variable, key) in processedParams.body"
          :key="`body-${key}`"
          class="mb-2.5"
        >
          <Input
            v-model="processedParams.body[key]"
            type="text"
            class="flex-1"
            :placeholder="
              t('WHATSAPP_TEMPLATES.PARSER.VARIABLE_PLACEHOLDER', {
                variable: key,
              })
            "
          />
          <div
            v-if="availableContextVariables.length"
            class="flex flex-wrap gap-1.5 mt-2"
          >
            <button
              v-for="[contextKey, contextValue] in availableContextVariables"
              :key="`body-${key}-${contextKey}`"
              type="button"
              class="px-2 py-1 text-xs rounded bg-n-alpha-black2 text-n-slate-11 hover:bg-n-alpha-3 focus:outline-none focus:ring-2 focus:ring-n-brand"
              @click="
                applyContextValue(processedParams.body, key, contextValue)
              "
            >
              {{ contextVariablePrompt(contextKey) }}
            </button>
          </div>
        </div>
      </div>

      <!-- Button Variables Section -->
      <div v-if="processedParams.buttons">
        <p class="mb-2.5 text-sm font-semibold">
          {{ t('WHATSAPP_TEMPLATES.PARSER.BUTTON_PARAMETERS') }}
        </p>
        <div
          v-for="(button, index) in processedParams.buttons"
          :key="`button-${index}`"
          class="flex items-center mb-2.5"
        >
          <Input
            v-model="processedParams.buttons[index].parameter"
            type="text"
            class="flex-1"
            :placeholder="t('WHATSAPP_TEMPLATES.PARSER.BUTTON_PARAMETER')"
          />
        </div>
      </div>

      <div v-if="hasCarouselParameters" class="mt-5">
        <p class="mb-2.5 text-sm font-semibold">
          {{ carouselTitle }}
        </p>
        <div
          v-for="(card, cardIndex) in carouselCards"
          :key="`carousel-parameters-${cardIndex}`"
          class="p-3 mb-3 rounded-lg bg-n-alpha-black2"
        >
          <p class="mb-3 text-sm font-medium text-n-slate-12">
            {{ carouselCardLabel(cardIndex) }}
          </p>
          <div v-if="processedParams.carousel?.[cardIndex]?.body">
            <div
              v-for="(variable, key) in processedParams.carousel[cardIndex]
                .body"
              :key="`carousel-body-${cardIndex}-${key}`"
              class="mb-2.5"
            >
              <Input
                v-model="processedParams.carousel[cardIndex].body[key]"
                type="text"
                class="flex-1"
                :placeholder="carouselVariablePlaceholder(key)"
              />
              <div
                v-if="availableContextVariables.length"
                class="flex flex-wrap gap-1.5 mt-2"
              >
                <button
                  v-for="[
                    contextKey,
                    contextValue,
                  ] in availableContextVariables"
                  :key="`card-${cardIndex}-${key}-${contextKey}`"
                  type="button"
                  class="px-2 py-1 text-xs rounded bg-n-alpha-black2 text-n-slate-11 hover:bg-n-alpha-3 focus:outline-none focus:ring-2 focus:ring-n-brand"
                  @click="
                    applyContextValue(
                      processedParams.carousel[cardIndex].body,
                      key,
                      contextValue
                    )
                  "
                >
                  {{ contextVariablePrompt(contextKey) }}
                </button>
              </div>
            </div>
          </div>
          <div v-if="processedParams.carousel?.[cardIndex]?.buttons">
            <div
              v-for="(button, buttonIndex) in processedParams.carousel[
                cardIndex
              ].buttons"
              :key="`carousel-button-${cardIndex}-${buttonIndex}`"
              class="mb-2.5"
            >
              <Input
                v-if="button"
                v-model="
                  processedParams.carousel[cardIndex].buttons[buttonIndex]
                    .parameter
                "
                type="text"
                class="flex-1"
                :placeholder="carouselButtonPlaceholder(buttonIndex)"
              />
              <div
                v-if="button && availableContextVariables.length"
                class="flex flex-wrap gap-1.5 mt-2"
              >
                <button
                  v-for="[
                    contextKey,
                    contextValue,
                  ] in availableContextVariables"
                  :key="`card-button-${cardIndex}-${buttonIndex}-${contextKey}`"
                  type="button"
                  class="px-2 py-1 text-xs rounded bg-n-alpha-black2 text-n-slate-11 hover:bg-n-alpha-3 focus:outline-none focus:ring-2 focus:ring-n-brand"
                  @click="
                    applyContextValue(
                      processedParams.carousel[cardIndex].buttons[buttonIndex],
                      'parameter',
                      contextValue
                    )
                  "
                >
                  {{ contextVariablePrompt(contextKey) }}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
      <p
        v-if="v$.$dirty && isFormInvalid"
        class="p-2.5 text-center rounded-md bg-n-ruby-9/20 text-n-ruby-9"
      >
        {{ $t('WHATSAPP_TEMPLATES.PARSER.FORM_ERROR_MESSAGE') }}
      </p>
    </div>

    <slot
      name="actions"
      :send-message="sendMessage"
      :replace-message="replaceMessage"
      :reset-template="resetTemplate"
      :go-back="goBack"
      :is-valid="!isFormInvalid"
      :disabled="isFormInvalid"
    />
  </div>
</template>

<style scoped>
.carousel-preview {
  position: relative;
}

.order-details-preview {
  padding: 0.875rem;
  border: 1px solid #b7c0ca;
  border-radius: 0.875rem;
  color: #283747;
  background: #fff;
}

.order-details-eyebrow {
  margin: 0 0 0.75rem;
  color: #607080;
  font-size: 0.625rem;
  font-weight: 600;
  letter-spacing: 0.14em;
  text-transform: uppercase;
}

.order-details-main {
  display: flex;
  gap: 0.625rem;
  align-items: center;
  margin-bottom: 0.75rem;
}

.order-details-icon {
  display: grid;
  width: 2.5rem;
  height: 2.5rem;
  place-items: center;
  border-radius: 0.375rem;
  color: #697786;
  background: #e8edf2;
}

.order-details-icon::before {
  content: '⌑';
  font-size: 1.5rem;
}

.order-details-title,
.order-details-subtitle {
  margin: 0;
}

.order-details-title {
  font-size: 0.875rem;
  font-weight: 700;
}

.order-details-subtitle {
  margin-top: 0.2rem;
  color: #607080;
  font-size: 0.75rem;
}

.order-details-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.55rem 0;
  border-top: 1px solid #d9e0e7;
  font-size: 0.75rem;
}

.order-details-qr {
  color: #4a5968;
}

.order-details-qr::before {
  content: '▦';
  font-size: 1.1rem;
}

.order-details-total {
  font-weight: 600;
}

.carousel-scroll {
  -ms-overflow-style: none;
  scrollbar-width: none;
  scroll-snap-type: x proximity;
}

.carousel-scroll::-webkit-scrollbar {
  display: none;
}

.carousel-card {
  scroll-snap-align: start;
}

.carousel-control {
  align-items: center;
  background: #252a32;
  border: 1px solid #48515e;
  border-radius: 9999px;
  color: #f5f7fa;
  display: flex;
  font-size: 1.5rem;
  height: 2rem;
  justify-content: center;
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  width: 2rem;
  z-index: 1;
}

.carousel-control:focus-visible {
  outline: 2px solid #3b82f6;
  outline-offset: 2px;
}

.carousel-control-left {
  left: 0.25rem;
}

.carousel-control-right {
  right: 0.25rem;
}
</style>
